import {FormProvider, useFieldArray, useForm} from 'react-hook-form'
import {EditDetailsSectionButtons} from '@/components/commons/edit-details-section-buttons/EditDetailsSectionButtons'
import {Button} from '@/components/ui/button/Button'
import {DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {PlusIcon} from '@/components/ui/icon/Icon'
import {OtherPathologiesFormSchema, ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {DEFAULT_OTHER_PATHOLOGY_VALUES} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import {zodResolver} from '@hookform/resolvers/zod'
import {useStoreProjectOtherPathologies} from '@/features/projects/queries/useStoreProjectOtherPathologies'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {ProjectOtherPathologiesFields} from '@/features/projects/components/project-other-pathologies-fields/ProjectOtherPathologiesFields'
import {ObjectKeys} from '@/utilities/helpers'

interface ProjectOtherPathologiesFormProps {
    projectId: string
    defaultValues?: ProjectFieldsSchema['otherPathologies']
    isDisabled: boolean
    isEditModeActive: boolean
    handleCancel: () => void
    handleEdit: () => void
}

export const ProjectOtherPathologiesForm = ({
    projectId,
    defaultValues,
    isDisabled,
    isEditModeActive,
    handleCancel,
    handleEdit
}: ProjectOtherPathologiesFormProps) => {
    const {t} = useTranslation()
    const form = useForm<OtherPathologiesFormSchema>({
        resolver: zodResolver(OtherPathologiesFormSchema),
        defaultValues: {
            otherPathologies: defaultValues || [DEFAULT_OTHER_PATHOLOGY_VALUES]
        }
    })

    const {fields, append, remove} = useFieldArray({
        control: form.control,
        name: 'otherPathologies'
    })

    const {mutate: storeProjectOtherPathologies, isPending} = useStoreProjectOtherPathologies({
        urlParams: {
            projectId
        },
        options: {
            onSuccess: async () => {
                await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT_OTHER_PATHOLOGIES, projectId]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_OTHER_PATHOLOGIES, projectId]})
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT_VALIDATORS, projectId]})
                handleCancel()
            }
        }
    })

    const onSubmit = form.handleSubmit(
        data => storeProjectOtherPathologies(data),
        errors => console.error(errors)
    )

    return (
        <FormProvider {...form}>
            <DetailsFieldsSection
                variant="neutral"
                title={t('projects:other_pathologies_card_title')}
                isDisabled={isDisabled}
                headingEndSlot={
                    <EditDetailsSectionButtons
                        isLoading={isPending}
                        isActive={isEditModeActive}
                        onCancel={handleCancel}
                        onEdit={handleEdit}
                        onConfirm={onSubmit}
                        isDisabled={!form.formState.isValid || !ObjectKeys(form.formState.dirtyFields).length}
                    />
                }
            >
                <Flexbox direction="column" gap={6} fullWidth>
                    {fields.map((field, index) => (
                        <ProjectOtherPathologiesFields
                            key={field.id}
                            index={index}
                            disabledPathologies={fields.map(f => f.diagnosisId)}
                            showTrashButton={fields.length > 1}
                            remove={remove}
                        />
                    ))}
                    {fields.length < 3 && (
                        <Button variant="secondary" size="sm" onClick={() => append(DEFAULT_OTHER_PATHOLOGY_VALUES)}>
                            <PlusIcon />
                            {t('projects:add_pathology')}
                        </Button>
                    )}
                </Flexbox>
            </DetailsFieldsSection>
        </FormProvider>
    )
}
